import { useOrganization } from '@clerk/react';
import GjsEditor from '@grapesjs/react';
import { Dialog } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import type { EditorConfig } from 'grapesjs';
import grapesjs, { usePlugin } from 'grapesjs';
import { useState } from 'react';
import { useDebounce } from 'src/hooks/useDebounce';
import { usePalletSsccEnabled } from 'src/hooks/usePalletSsccEnabled';
import { appLinks } from 'src/routing/appLinks';
import { useDocumentEditorSearch } from 'src/routing/routeSearch';
import { default as stylePlugin } from '../../../plugins/styles';
import DocumentEditorPreview from '../../components/DocumentEditorPreview';
import { DocumentEditorWorkspace } from '../../components/DocumentEditorWorkspace';
import DocumentStorageLoadModal from '../../components/DocumentStorageLoadModal';
import DocumentStorageSaveModal from '../../components/DocumentStorageSaveModal';
import useDocumentStorage from '../../hooks/useDocumentStorage';
import { default as accountingComponentsPlugin } from '../../plugins/accountingComponents';
import { default as componentsPlugin } from '../../plugins/components';
import { default as functionsPlugin } from '../../plugins/functions';
import { default as layoutPlugin } from '../../plugins/layout';
import { default as tablePlugin } from '../../plugins/table';
import { default as tablev2 } from '../../plugins/tablev2';
import { default as variablePlugin } from '../../plugins/variables';
import {
  type DocumentOptions,
  documentTypeOptions,
  documentVariables,
  type GenerationOptions,
} from './documentEditorOptions';

const gjsOptions: EditorConfig = {
  height: '100vh',
  storageManager: { type: 'hybrid', autosave: true, autoload: true },
};

const DocumentEditorPage = ({ new: isNew, name, type, entity, lookup }) => {
  const { organization } = useOrganization();
  const isPalletSsccEnabled = usePalletSsccEnabled();
  const [modalOpen, setModalOpen] = useState(null);

  const initialDocumentType = documentTypeOptions.find((option) => option.entity === entity && option.type === type);

  const [documentType, setDocumentType] = useState<DocumentOptions | null>(initialDocumentType);
  const [documentName, setDocumentName] = useState(name);
  const [generationOptions, setGenerationOptions] = useState<GenerationOptions | null>(null);
  const [queryVariables, setQueryVariables] = useState<Record<string, unknown>>(
    lookup ? JSON.parse(decodeURIComponent(lookup)) : {}
  );
  const [storageSaveModalOpen, setStorageSaveModalOpen] = useState(false);
  const [storageLoadModalOpen, setStorageLoadModalOpen] = useState(false);
  const [templateId, setTemplateId] = useState<number | null | undefined>(null);
  const [showInBulkPrintMenu, setShowInBulkPrintMenu] = useState(false);

  const { plugin: storagePlugin } = useDocumentStorage({
    isNew,
    documentConfig: { name: documentName, type: documentType?.type, entity: documentType?.entity },
    setStorageSaveModalOpen,
    setStorageLoadModalOpen,
    setTemplateId,
    setShowInBulkPrintMenu,
  });

  const search = useDocumentEditorSearch();
  const navigate = useNavigate();

  const debounceNav = useDebounce(
    (variables) => {
      const encoded = encodeURIComponent(JSON.stringify(variables));

      void navigate(appLinks.documentEditor({ ...search, lookup: encoded }, { replace: true }));
    },
    500,
    null,
    [navigate, search]
  );

  return (
    <>
      <Dialog
        open={!!modalOpen}
        onClose={() => {
          setModalOpen(null);
        }}
      >
        <DocumentEditorPreview
          open={!!modalOpen}
          css={modalOpen?.css}
          html={modalOpen?.html}
          entity={documentType?.entity}
          type={documentType?.type}
          queryVariables={queryVariables}
          templateId={templateId}
          generationOptions={generationOptions}
        />
      </Dialog>
      <GjsEditor
        grapesjs={grapesjs}
        grapesjsCss="https://unpkg.com/grapesjs/dist/css/grapes.min.css"
        options={gjsOptions}
        plugins={[
          usePlugin(storagePlugin),
          usePlugin(componentsPlugin),
          usePlugin(accountingComponentsPlugin),
          usePlugin(layoutPlugin(setModalOpen, organization.imageUrl)),
          usePlugin(tablePlugin),
          usePlugin(stylePlugin),
          usePlugin(functionsPlugin),
          usePlugin(variablePlugin(documentType?.entity, { isPalletSsccEnabled })),
          usePlugin(tablev2),
        ]}
      >
        <DocumentStorageSaveModal
          open={storageSaveModalOpen}
          onClose={() => setStorageSaveModalOpen(false)}
          documentName={documentName}
          setDocumentName={setDocumentName}
          documentType={documentType}
          setDocumentType={setDocumentType}
          generationOptions={generationOptions}
          setGenerationOptions={setGenerationOptions}
          showInBulkPrintMenu={showInBulkPrintMenu}
          setShowInBulkPrintMenu={setShowInBulkPrintMenu}
        />
        <DocumentStorageLoadModal
          open={storageLoadModalOpen}
          onClose={() => setStorageLoadModalOpen(false)}
          setDocumentName={setDocumentName}
          setDocumentType={(type) => {
            setQueryVariables({});
            setDocumentType(type);
          }}
          setGenerationOptions={setGenerationOptions}
          setShowInBulkPrintMenu={setShowInBulkPrintMenu}
        />
        <DocumentEditorWorkspace
          queryVariables={queryVariables}
          onQueryVariablesChange={(variables) => {
            debounceNav(variables);

            setQueryVariables(variables);
          }}
          documentVariables={(documentType ? documentVariables[documentType.entity] : undefined) ?? []}
        />
      </GjsEditor>
    </>
  );
};

export default DocumentEditorPage;
